import type { SVGProps } from 'react'

/**
 * Line icons ported 1:1 from the concept (rehrig_analytics_ui.html) — exact
 * paths and stroke weights. All inherit currentColor; size via `size` prop
 * (defaults match the concept's usage).
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number }

const base = (size: number, { size: sizeProp, ...props }: IconProps) => ({
  width: sizeProp ?? size,
  height: sizeProp ?? size,
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  ...props,
})

const filled = (size: number, { size: sizeProp, ...props }: IconProps) => ({
  width: sizeProp ?? size,
  height: sizeProp ?? size,
  fill: 'currentColor',
  ...props,
})

/* The ico-* classes below are inert on their own — sidebar rows opt into the
 * hover micro-animations by wrapping the icon in `.side-ico` (globals.css). */

export function SearchIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(18, props)} strokeWidth={1.8}>
      <g className="ico-lens">
        <circle cx="10.5" cy="10.5" r="7.5" />
        <path d="m21 21-5.2-5.2" />
      </g>
    </svg>
  )
}

export function NewChatIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(18, props)} strokeWidth={2}>
      <g transform="translate(12 12)">
        <path d="M-2,-8 C-3.864,-8 -4.796,-8 -5.531,-7.696 C-6.511,-7.29 -7.29,-6.511 -7.696,-5.531 C-8,-4.796 -8,-3.864 -8,-2 L-8,1.6 C-8,3.84 -8,4.96 -7.564,5.816 C-7.181,6.569 -6.569,7.181 -5.816,7.564 C-4.96,8 -3.84,8 -1.6,8 L2,8 C3.864,8 4.796,8 5.531,7.696 C6.511,7.29 7.29,6.511 7.696,5.531 C8,4.796 8,3.864 8,2" />
      </g>
      {/* Positioning stays on the outer group's ATTRIBUTE transform — the
          hover animation's CSS transform on the inner group would otherwise
          replace it and teleport the pencil. */}
      <g transform="translate(14.561 9.439)">
        <g className="ico-pencil">
          <path d="M4.939,-1.939 L-2.121,5.121 C-2.402,5.402 -2.784,5.561 -3.182,5.561 L-5.561,5.561 L-5.561,3.182 C-5.561,2.784 -5.402,2.402 -5.121,2.121 L1.939,-4.939 C2.767,-5.767 4.111,-5.767 4.939,-4.939 C5.767,-4.111 5.767,-2.767 4.939,-1.939Z" />
        </g>
      </g>
    </svg>
  )
}

/** Two overlapping round bubbles with pointed tails — ORIGINAL drawing
 * (user reference: icons8.com/icon/RXH0x5wZrq5V, whose SVG is paid-only and
 * PNG needs attribution, so the paths here are ours: front bubble = circle +
 * tail wedge; back bubble = the arc peeking out behind it + its own tail).
 * Hover: the bubbles "take turns speaking" — front pops, then back
 * (motion re-created from the reference's animated preview, our keyframes). */
export function ChatsIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(18, props)} strokeWidth={1.7}>
      <g className="ico-bubble-front">
        <path d="M7.68 15.01 A6.3 6.3 0 1 0 3.64 11.86 L2.60 15.40 Z" />
      </g>
      <g className="ico-bubble-back">
        <path d="M16.17 7.62 A5.8 5.8 0 0 1 18.81 16.24 L20.30 20.00 L15.98 18.45 A5.8 5.8 0 0 1 9.08 16.07" />
      </g>
    </svg>
  )
}

/** Plain chat bubble (palette rows). */
export function ChatBubbleIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.7}>
      <path d="M21 12a8 8 0 0 1-8 8H4l1.5-3A8 8 0 1 1 21 12Z" />
    </svg>
  )
}

export function ProjectsIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(18, props)} strokeWidth={1.7}>
      <g className="ico-folder">
        <path d="M3 8.5A2.5 2.5 0 0 1 5.5 6h3.09a2 2 0 0 1 1.42.59L11.5 8h7A2.5 2.5 0 0 1 21 10.5v6A2.5 2.5 0 0 1 18.5 19h-13A2.5 2.5 0 0 1 3 16.5Z" />
        <path d="M3 10.5h18" />
      </g>
    </svg>
  )
}

export function DashboardsIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(18, props)} strokeWidth={1.7}>
      <rect className="ico-tile" x="3" y="3" width="8" height="6" rx="2.2" />
      <rect className="ico-tile" x="3" y="12" width="8" height="9" rx="2.2" />
      <rect className="ico-tile" x="13" y="3" width="8" height="9" rx="2.2" />
      <rect className="ico-tile" x="13" y="15" width="8" height="6" rx="2.2" />
    </svg>
  )
}

export function KnowledgeGraphIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(18, props)} strokeWidth={1.7}>
      <circle cx="5" cy="6" r="2.5" />
      <circle cx="19" cy="6" r="2.5" />
      <circle cx="12" cy="18" r="2.5" />
      <circle cx="12" cy="10.5" r="2" />
      <path d="M7.2 7.3 10.3 9.6M16.8 7.3 13.7 9.6M12 12.5v3" />
    </svg>
  )
}

export function SidebarIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(18, props)} strokeWidth={1.8}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
    </svg>
  )
}

/** History — counterclockwise circular arrow + clock hand (collapsed-rail
 * chat history trigger, user reference 2026-07-27). Original drawing. */
export function HistoryIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(18, props)} strokeWidth={1.7}>
      <path d="M3.2 12a8.8 8.8 0 1 0 8.8 -8.8c-2.55 0-4.9 1.1-6.55 2.85L3.4 8.1" />
      <path d="M3.2 3.4v4.7h4.7" />
      <path d="M12 7.6V12l-2.9 1.7" />
    </svg>
  )
}

/* — sidebar recent-dashboard icons — */
export function GridIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.7}>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </svg>
  )
}

export function RouteIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.7}>
      <circle cx="6" cy="19" r="2.5" />
      <circle cx="18" cy="5" r="2.5" />
      <path d="M8.5 19H15a3.5 3.5 0 0 0 0-7H9a3.5 3.5 0 0 1 0-7h6.5" />
    </svg>
  )
}

export function RfidIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.7}>
      <path d="M4.5 9.5a8 8 0 0 1 15 0" />
      <path d="M7.5 11.5a5 5 0 0 1 9 0" />
      <circle cx="12" cy="15" r="1.6" />
      <path d="M12 16.6V21" />
    </svg>
  )
}

/* — composer — */
export function PlusIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(20, props)} strokeWidth={1.8}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

export function MicIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(20, props)} strokeWidth={1.8}>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </svg>
  )
}

export function SendIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(18, props)} strokeWidth={2.2}>
      <path d="M12 19V5m0 0-6 6m6-6 6 6" />
    </svg>
  )
}

/* — chrome / menus — */
export function ChevronDownIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(12, props)} strokeWidth={2}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(15, props)} strokeWidth={2}>
      <path d="m9 6 6 6-6 6" />
    </svg>
  )
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={2}>
      <path d="m15 6-6 6 6 6" />
    </svg>
  )
}

export function UpDownIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.8}>
      <path d="m8 9 4-4 4 4M8 15l4 4 4-4" />
    </svg>
  )
}

export function DotsIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...filled(16, props)}>
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  )
}

export function DotsLargeIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...filled(20, props)}>
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  )
}

export function FileIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(20, props)} strokeWidth={1.7}>
      <path d="M13 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" />
      <path d="M13 3v6h6" />
    </svg>
  )
}

export function SheetIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(15, props)} strokeWidth={2}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M9 4v16" />
    </svg>
  )
}

export function ShareIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(20, props)} strokeWidth={1.7}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
    </svg>
  )
}

export function LinkIcon({ size, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size ?? 20}
      height={size ?? 20}
      fill="none"
      {...props}
    >
      <path
        d="M7.0483 9.87862L5.63424 11.2927C3.68162 13.2453 3.68162 16.4114 5.63424 18.364C7.58681 20.3163 10.752 20.3163 12.7045 18.364L14.1196 16.9499L15.5337 18.364L14.1196 19.778C11.3859 22.5117 6.95287 22.5117 4.2192 19.778C1.48589 17.0444 1.48581 12.6122 4.2192 9.87862L5.63424 8.46456L7.0483 9.87862ZM16.2417 9.16964L9.17135 16.2409L7.75631 14.8269L14.8276 7.75558L16.2417 9.16964ZM9.87642 4.2214C12.61 1.48801 17.0422 1.48809 19.7758 4.2214C22.5095 6.95506 22.5095 11.3881 19.7758 14.1218L18.3618 15.5358L16.9477 14.1218L18.3618 12.7067C20.3141 10.7542 20.3141 7.58901 18.3618 5.63643C16.4092 3.68381 13.2431 3.68381 11.2905 5.63643L9.87642 7.0505L8.46236 5.63643L9.87642 4.2214Z"
        fill="currentColor"
      />
    </svg>
  )
}

export function PencilIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(15, props)} strokeWidth={1.8}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  )
}

export function StarIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(15, props)} strokeWidth={1.8}>
      <path d="M12 3l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 18.8 6.2 21.9l1.1-6.5L2.6 9.8l6.5-.9Z" />
    </svg>
  )
}

export function FolderIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(15, props)} strokeWidth={1.8}>
      <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2.2h9A1.5 1.5 0 0 1 21 9.7v7.8A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5Z" />
    </svg>
  )
}

export function ExportIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(15, props)} strokeWidth={1.8}>
      <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  )
}

export function TrashIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(15, props)} strokeWidth={1.8}>
      <path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m2 0v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7" />
    </svg>
  )
}

export function ArchiveIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(15, props)} strokeWidth={1.8}>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" />
    </svg>
  )
}

export function CopyIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={2}>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  )
}

export function CheckIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(12, props)} strokeWidth={2.4}>
      <path d="m5 13 4 4L19 7" />
    </svg>
  )
}

/** Toast status icon (concept CHECK_CIRCLE). */
export function CheckCircleIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(22, props)} strokeWidth={1.7}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.4 12.2 2.4 2.4 4.8-5" />
    </svg>
  )
}

export function ThumbsUpIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={2}>
      <path d="M7 20H3V9H5.5C6.32843 9 7 9.67157 7 10.5V20ZM7 20H16.2062C17.8403 20 19.257 18.8692 19.6192 17.2757L20.8059 12.0541C21.1614 10.4896 19.9724 9 18.3681 9H14.5L15.1142 5.31454C15.3162 4.10294 14.3818 3 13.1535 3C12.4402 3 11.7816 3.38222 11.4277 4.00155L8.43188 9.24421C8.16482 9.71157 7.6678 10 7.12952 10H6.91465" />
    </svg>
  )
}

export function ThumbsDownIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={2}>
      <path d="M7 4H3V15H5.5C6.32843 15 7 14.3284 7 13.5V4ZM7 4H16.2062C17.8403 4 19.257 5.13082 19.6192 6.72433L20.8059 11.9459C21.1614 13.5104 19.9724 15 18.3681 15H14.5L15.1142 18.6855C15.3162 19.8971 14.3818 21 13.1535 21C12.4402 21 11.7816 20.6178 11.4277 19.9984L8.43188 14.7558C8.16482 14.2884 7.6678 14 7.12952 14H6.91465" />
    </svg>
  )
}

export function RegenerateIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={2}>
      <path d="M4 20V15H4.31241M4.31241 15H9M4.31241 15C5.51251 18.073 8.50203 20.25 12 20.25C15.8582 20.25 19.0978 17.6016 20 14.0236M20 4V9H19.6876M19.6876 9H15M19.6876 9C18.4875 5.92698 15.498 3.75 12 3.75C8.14184 3.75 4.90224 6.3984 4 9.9764" />
    </svg>
  )
}

export function EditIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={2}>
      <path d="M18.25 5.75C16.8693 4.36929 14.6307 4.36929 13.25 5.75L10.125 8.875L5.52404 13.476C4.86236 14.1376 4.45361 15.0104 4.36889 15.9423L4 20.0001L8.0578 19.6311C8.98967 19.5464 9.86234 19.1377 10.524 18.476L18.25 10.75C19.6307 9.36929 19.6307 7.13071 18.25 5.75Z" />
      <path d="M12.5 7.5L16.5 11.5" />
    </svg>
  )
}

export function FollowupIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={2}>
      <polyline points="15 10 20 15 15 20" />
      <path d="M4 4v7a4 4 0 0 0 4 4h12" />
    </svg>
  )
}

export function BackIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(18, props)} strokeWidth={1.9}>
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  )
}

export function CloseIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(18, props)} strokeWidth={2}>
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  )
}

export function SettingsIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.7}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  )
}

export function GlobeIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.8}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
    </svg>
  )
}

export function LightbulbIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.8}>
      <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.53.4.85 1.02.85 1.68V16h5.5v-.52c0-.66.32-1.28.85-1.68A6 6 0 0 0 12 3Z" />
    </svg>
  )
}

export function HelpIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.8}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 0 1 4.5 1.5c0 1.7-2.5 2-2.5 3.5" />
      <path d="M12 17h.01" />
    </svg>
  )
}

export function InfoIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.8}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  )
}

export function LogoutIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.8}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </svg>
  )
}

export function UserIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(17, props)} strokeWidth={1.7}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  )
}

export function ChartIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(17, props)} strokeWidth={1.7}>
      <path d="M5 20V10M12 20V4M19 20v-7" />
    </svg>
  )
}

export function CardIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(17, props)} strokeWidth={1.7}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  )
}

export function CodeIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(17, props)} strokeWidth={1.7}>
      <path d="m8 8-4 4 4 4M16 8l4 4-4 4" />
    </svg>
  )
}

export function SkillsIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(17, props)} strokeWidth={1.7}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 9h5M8 13h8M8 17h6" />
    </svg>
  )
}

export function ConnectorsIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(17, props)} strokeWidth={1.7}>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="14" y="4" width="6" height="6" rx="3" />
      <rect x="4" y="14" width="7" height="6" rx="1.5" />
      <rect x="14" y="13" width="7" height="7" rx="1.5" />
    </svg>
  )
}

export function PluginsIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(17, props)} strokeWidth={1.7}>
      <path d="M9 3v4M15 3v4M8 7h8v5a4 4 0 0 1-8 0Z" />
      <path d="M12 16v5" />
    </svg>
  )
}

export function CollapseIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={2}>
      <path d="m7 20 5-5 5 5" />
      <path d="m7 4 5 5 5-5" />
    </svg>
  )
}

export function WrapIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={2}>
      <path d="M3 6h18M3 12h13a3 3 0 0 1 0 6h-3m0 0 2-2m-2 2 2 2M3 18h5" />
    </svg>
  )
}

export function PlayIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...filled(14, props)}>
      <path d="M7 5v14l11-7z" />
    </svg>
  )
}

export function PauseIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...filled(14, props)}>
      <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
    </svg>
  )
}

export function DownloadIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(17, props)} strokeWidth={1.8}>
      <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  )
}

export function ExpandIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.9}>
      <path d="M9 21H3v-6M21 3h-6M3 21 10 14M21 3l-7 7" />
    </svg>
  )
}

export function ExpandAltIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.8}>
      <path d="M9 21H3v-6M15 3h6v6M3 21 10 14M21 3l-7 7" />
    </svg>
  )
}

/* — New design icons — */
export function MenuIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(20, props)} strokeWidth={2}>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  )
}

export function HomeIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(24, props)} strokeWidth={1.7}>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 22V12h6v10" />
    </svg>
  )
}

export function DataIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(24, props)} strokeWidth={1.7}>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14c0 1.7 4 3 9 3s9-1.3 9-3V5" />
      <path d="M3 12c0 1.7 4 3 9 3s9-1.3 9-3" />
    </svg>
  )
}

export function ReportsIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(24, props)} strokeWidth={1.7}>
      <path d="M13 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M13 3v6h6M8 13h8M8 17h8" />
    </svg>
  )
}

export function ClockIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.8}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  )
}

/** Code-block collapse toggle while collapsed — chevrons point outward. */
export function UncollapseIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={2}>
      <path d="m7 15 5 5 5-5" />
      <path d="m7 9 5-5 5 5" />
    </svg>
  )
}

/** Message-actions kebab — hairline stroked dots (concept msg-more-btn). */
export function DotsSmallIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={2}>
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </svg>
  )
}

/** Search glyph used by list pages, settings search and the palette head. */
export function SearchAltIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={2}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  )
}

/** Add-menu Research item — search glass with compass needle. */
export function ResearchIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.7}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
      <path d="m14.5 7.5-2 5-5 2 2-5Z" />
    </svg>
  )
}

/** File-panel / file-card chip glyph for pdf docs (concept FILE_ICONS.pdf). */
export function DocIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(15, props)} strokeWidth={2}>
      <path d="M13 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" />
      <path d="M13 3v6h6" />
    </svg>
  )
}

/** Files-list header chip glyph (concept FOLDER_ICON). */
export function FolderChipIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(15, props)} strokeWidth={2}>
      <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2.2h9A1.5 1.5 0 0 1 21 9.7v7.8A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5Z" />
    </svg>
  )
}

/** File-card expand toggle while its preview is open (concept COLLAPSE_ICON). */
export function CollapseWindowIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.9}>
      <path d="M10 20v-6H4M20 4h-6v6M4 20l6-6M20 4l-6 6" />
    </svg>
  )
}

/** Settings appearance segmented control — System. */
export function MonitorIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.7}>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  )
}

/** Settings appearance segmented control — Light. */
export function SunIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.7}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19" />
    </svg>
  )
}

/** Settings appearance segmented control — Dark. */
export function MoonIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...base(16, props)} strokeWidth={1.7}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  )
}
